import { useSyncExternalStore } from "react";
import type { AlertInboxItem, AlertSentItem } from "../types";

/**
 * Queue of ANNOUNCE alerts waiting to be shown as a popup. Fed by the socket
 * (`alert:new` with mode ANNOUNCE) and by the inbox's first page
 * (`pendingAnnouncements`: still-unread announcements, so they pop on login).
 * One popup at a time, in arrival order; an alert pops once per page load
 * even if both feeds deliver it.
 *
 * `viewed` is the alert someone clicked in the Alert box, silent or announced,
 * received or sent. It shows in the same popup, ahead of the queue, and closing
 * it acknowledges nothing: the row already marked itself read on the click.
 */
let queue: AlertInboxItem[] = [];
let viewed: AlertInboxItem | AlertSentItem | null = null;
const seen = new Set<number>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

export function enqueueAnnouncement(item: AlertInboxItem | null | undefined) {
  if (!item || !Number.isFinite(item.id) || seen.has(item.id)) return;
  seen.add(item.id);
  queue = [...queue, item];
  notify();
}

/** The popup was acknowledged (or the alert was taken back). */
export function dismissAnnouncement(id?: number) {
  const next = id == null ? queue.slice(1) : queue.filter((a) => a.id !== id);
  if (next.length === queue.length) return;
  queue = next;
  notify();
}

/** Open an alert from the Alert box in the popup. */
export function viewAlert(item: AlertInboxItem | AlertSentItem) {
  viewed = item;
  notify();
}

export function closeViewedAlert() {
  if (!viewed) return;
  viewed = null;
  notify();
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
const getSnapshot = () => queue;
const getViewed = () => viewed;

/** Reactive view for AlertAnnounceHost: the head of the queue + how many wait. */
export function useAnnouncementQueue(): AlertInboxItem[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Reactive view for AlertAnnounceHost: the alert opened from the box, if any. */
export function useViewedAlert(): AlertInboxItem | AlertSentItem | null {
  return useSyncExternalStore(subscribe, getViewed, getViewed);
}
